import { useEffect, useMemo, useState } from "react";
import { SEO } from "../components/SEO";
import { Link } from "react-router-dom";
import {
  Award,
  Building2,
  ChevronRight,
  ExternalLink,
  Globe,
  Loader2,
  Star,
  Users,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import odooApi from "../services/odooApi";

const PARTNER_CATEGORY_PRIORITY = ["partner", "insurance", "payment", "certification"];

function buildWebsiteUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }
  return `https://${raw}`;
}

function getPartnerCategoryLabel(category, t) {
  switch (category) {
    case "insurance":
      return t("partners.categoryInsurance", "Insurance Partner");
    case "payment":
      return t("partners.categoryPayment", "Payment Partner");
    case "certification":
      return t("partners.categoryCertification", "Certification");
    case "partner":
    default:
      return t("partners.categoryPartner", "Business Partner");
  }
}

function getPartnerDescription(category, t) {
  switch (category) {
    case "insurance":
      return t(
        "partners.categoryInsuranceDesc",
        "Trusted coverage and risk-management partners supporting the rental journey.",
      );
    case "payment":
      return t(
        "partners.categoryPaymentDesc",
        "Secure payment providers integrated into the Velaris customer experience.",
      );
    case "certification":
      return t(
        "partners.categoryCertificationDesc",
        "Verified certifications and trust signals maintained from the Odoo CMS.",
      );
    case "partner":
    default:
      return t(
        "partners.categoryPartnerDesc",
        "Business partners managed from Odoo and surfaced directly on the website.",
      );
  }
}

export const PartnersPage = () => {
  const { t } = useLanguage();
  const [partners, setPartners] = useState([]);
  const [selectedType, setSelectedType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchPartners = async () => {
      setLoading(true);
      setLoadFailed(false);

      try {
        const partnerLogos = await odooApi.getPartnerLogos();
        if (!isMounted) {
          return;
        }
        setPartners(partnerLogos);
      } catch (error) {
        console.error("Failed to load partners from Odoo:", error);
        if (!isMounted) {
          return;
        }
        setPartners([]);
        setLoadFailed(true);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPartners();

    return () => {
      isMounted = false;
    };
  }, []);

  const partnerTypes = useMemo(() => {
    const presentTypes = new Set(partners.map((partner) => partner.category).filter(Boolean));
    const orderedTypes = PARTNER_CATEGORY_PRIORITY.filter((type) => presentTypes.has(type));
    const extraTypes = Array.from(presentTypes).filter(
      (type) => !PARTNER_CATEGORY_PRIORITY.includes(type),
    );
    return ["all", ...orderedTypes, ...extraTypes];
  }, [partners]);

  const filteredPartners = selectedType === "all"
    ? partners
    : partners.filter((partner) => partner.category === selectedType);

  const featuredPartners = partners.slice(0, 3);
  const partnerBenefits = [
    {
      icon: Award,
      title: t("partners.benefitQualityTitle", "Quality Assurance"),
      description: t(
        "partners.benefitQualityDescription",
        "All partners undergo rigorous vetting to ensure the highest standards.",
      ),
    },
    {
      icon: Users,
      title: t("partners.benefitCustomerFirstTitle", "Customer First"),
      description: t(
        "partners.benefitCustomerFirstDescription",
        "Our partners share our commitment to exceptional customer service.",
      ),
    },
    {
      icon: Globe,
      title: t("partners.benefitNetworkTitle", "Wide Network"),
      description: t(
        "partners.benefitNetworkDescription",
        "Access to an extensive network of premium services across UAE.",
      ),
    },
    {
      icon: Star,
      title: t("partners.benefitTrustedTitle", "Trusted Integrations"),
      description: t(
        "partners.benefitTrustedDescription",
        "Insurance, payment, and business partners are managed directly from Odoo.",
      ),
    },
  ];

  return (
    <>
      <SEO
        title={t("partners.pageTitle", "Our Partners | Velaris Royal Car Rental")}
        description={t("partners.pageDescription", "Discover our trusted partners in the luxury car rental industry. Premium dealerships, suppliers, and service providers in Dubai and UAE.")}
        keywords="car rental partners, luxury car suppliers dubai, automotive partners uae"
        breadcrumbs={[
          { name: t("nav.home", "Home"), url: "/" },
          { name: t("nav.partners", "Partners") },
        ]}
      />

      <div className="min-h-screen bg-white dark:bg-gray-950" data-testid="partners-page">
        <section className="bg-black py-20 md:py-28 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&h=600&fit=crop"
              alt={t("about.heroAlt", "Luxury cars")}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-block bg-gold-gradient text-black px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-semibold mb-6">
              {t("partners.subtitle", "Trusted Network")}
            </span>
            <h1 className="font-[var(--font-display)] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t("partners.title", "Our Partners")}
            </h1>
            <p className="text-white/70 max-w-2xl mx-auto text-lg">
              {t("partners.description", "We collaborate with the finest automotive partners to deliver an unparalleled luxury car rental experience.")}
            </p>
          </div>
        </section>

        <section className="py-16 bg-gray-50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {partnerBenefits.map((benefit, idx) => {
                const Icon = benefit.icon;
                return (
                  <div key={idx} className="text-center">
                    <div className="w-16 h-16 bg-gold-gradient rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-black" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {loading && (
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-3 text-gray-500 dark:text-gray-300">
              <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
              <span>{t("partners.loading", "Loading partners from Odoo...")}</span>
            </div>
          </section>
        )}

        {!loading && loadFailed && (
          <section className="py-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-5 text-center text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
                {t("partners.loadError", "The partners section could not be loaded from Odoo right now.")}
              </div>
            </div>
          </section>
        )}

        {!loading && featuredPartners.length > 0 && (
          <section className="py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-semibold">
                  {t("partners.featuredTitle", "Premium Collaborations")}
                </span>
                <h2 className="font-[var(--font-display)] text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mt-4">
                  {t("partners.featuredPartners", "Featured Partners")}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {featuredPartners.map((partner) => {
                  const websiteUrl = buildWebsiteUrl(partner.website);

                  return (
                    <div
                      key={partner.id}
                      className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-100 dark:border-gray-700 hover:border-[#D4AF37] transition-all hover:shadow-xl group"
                      data-testid={`partner-${partner.id}`}
                    >
                      <div className="flex items-center justify-center h-24 mb-6 rounded-2xl bg-gray-50 dark:bg-gray-900">
                        {partner.logo ? (
                          <img
                            src={partner.logo}
                            alt={partner.name}
                            className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
                          />
                        ) : (
                          <span className="text-2xl font-bold text-[#D4AF37]">
                            {partner.name.slice(0, 2).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between mb-3 gap-3">
                        <h3 className="font-[var(--font-display)] text-xl font-semibold text-gray-900 dark:text-white">
                          {partner.name}
                        </h3>
                        <span className="text-xs bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-1 rounded-full text-center">
                          {getPartnerCategoryLabel(partner.category, t)}
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                        {getPartnerDescription(partner.category, t)}
                      </p>
                      {websiteUrl ? (
                        <a
                          href={websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#C5A028] font-medium text-sm"
                        >
                          {t("partners.visitWebsite", "Visit Website")}
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
                          <Globe className="w-4 h-4" />
                          {t("partners.managedInCms", "Managed in Odoo CMS")}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {!loading && (
          <section className="py-16 md:py-24 bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                <div>
                  <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-semibold">
                    {t("partners.ourNetwork", "Our Network")}
                  </span>
                  <h2 className="font-[var(--font-display)] text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mt-2">
                    {t("partners.allPartners", "All Partners")}
                  </h2>
                </div>

                {partnerTypes.length > 1 && (
                  <div className="flex flex-wrap gap-2">
                    {partnerTypes.map((type) => (
                      <button
                        key={type}
                        onClick={() => setSelectedType(type)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          selectedType === type
                            ? "bg-[#D4AF37] text-black"
                            : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        }`}
                      >
                        {type === "all" ? t("partners.all", "All") : getPartnerCategoryLabel(type, t)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {filteredPartners.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredPartners.map((partner) => {
                    const websiteUrl = buildWebsiteUrl(partner.website);

                    return (
                      <div
                        key={partner.id}
                        className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-100 dark:border-gray-700 hover:border-[#D4AF37]/50 transition-all flex items-center gap-4"
                      >
                        <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
                          {partner.logo ? (
                            <img
                              src={partner.logo}
                              alt={partner.name}
                              className="max-h-14 max-w-full object-contain"
                            />
                          ) : (
                            <span className="text-xl font-bold text-[#D4AF37]">
                              {partner.name.slice(0, 2).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                            {partner.name}
                          </h3>
                          <p className="text-sm text-[#D4AF37]">
                            {getPartnerCategoryLabel(partner.category, t)}
                          </p>
                          {websiteUrl ? (
                            <a
                              href={websiteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-gray-500 hover:text-[#D4AF37] transition-colors"
                            >
                              {t("partners.learnMore", "Learn more")} {"->"}
                            </a>
                          ) : (
                            <span className="text-sm text-gray-400 dark:text-gray-500">
                              {t("partners.managedInCms", "Managed in Odoo CMS")}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-300 px-6 py-12 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  {t("partners.empty", "No active partners were found in Odoo.")}
                </div>
              )}
            </div>
          </section>
        )}

        <section className="py-16 md:py-24 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Building2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
            <h2 className="font-[var(--font-display)] text-3xl sm:text-4xl font-bold text-white mb-4">
              {t("partners.becomePartner", "Become a Partner")}
            </h2>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto">
              {t("partners.becomePartnerDesc", "Join our network of premium partners and connect with discerning customers seeking luxury automotive experiences.")}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 btn-gold px-8 py-4 text-black font-bold uppercase tracking-wider"
            >
              {t("partners.contactUs", "Contact Us")}
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default PartnersPage;
